import { formatPercent } from '@parlay-picker/shared/formatting';

// =============================================================================
// Types
// =============================================================================

export interface Bet {
  gameDate?: string | Date;
  date?: string | Date;
  createdAt?: string | Date;
  created_at?: string | Date;
  returned?: number;
  actualPayout?: number | string;
  stake?: number | string;
  result?: string;
  name?: string;
  league?: string;
  legs?: number;
  legCount?: number;
  odds?: string | number;
  totalOdds?: string | number;
  finalOdds?: string | number;
}

export interface Leg {
  player: string;
  prop: string;
  hit?: boolean | null;
  over?: boolean;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export interface BetStats {
  totalWagered: number;
  totalReturned: number;
  netPL: number;
  wins: number;
  losses: number;
  winRate: string;
  roi: string;
  biggestWin: { amount: number; name: string | undefined } | null;
  biggestLoss: { amount: number; name: string | undefined } | null;
  dateRange: DateRange | null;
}

export interface GroupStats {
  wagered: number;
  returned: number;
  wins: number;
  total: number;
}

export interface PlayerStats {
  hits: number;
  total: number;
}

export interface LegStats {
  totalLegs: number;
  settledLegs: number;
  pendingLegs: number;
  totalHits: number;
  totalMisses: number;
  hitRate: string | number;
  overHitRate: string | number;
  underHitRate: string | number;
  overLegs: Leg[];
  underLegs: Leg[];
  byPlayer: Record<string, PlayerStats>;
  byPropType: Record<string, PlayerStats>;
}

// =============================================================================
// Helper Functions
// =============================================================================

const getBetDate = (b: Bet): Date | null => {
  const date = b.gameDate || b.date || b.createdAt || b.created_at;
  return date ? new Date(date) : null;
};

const getReturned = (b: Bet): number => Number(b.returned ?? b.actualPayout ?? 0);
const getStake = (b: Bet): number => Number(b.stake ?? 0);
const getProfit = (b: Bet): number => getReturned(b) - getStake(b);

/**
 * Determines if a bet is a win.
 * - result === 'won' is explicit win
 * - result === 'push' is NOT a win (stake refunded, no profit)
 * - returned > stake means profit was made (win)
 * - returned === stake with no explicit result could be push or pending
 */
const isWin = (b: Bet): boolean => {
  // Explicit result takes precedence
  if (b.result === 'won') return true;
  if (b.result === 'push' || b.result === 'lost' || b.result === 'pending') return false;
  // No explicit result - check if profit was made (returned > stake = win)
  return getReturned(b) > getStake(b);
};
const getLegCount = (b: Bet): number => Number(b.legs ?? b.legCount ?? 0);

const getOdds = (b: Bet): number => {
  const odds = b.finalOdds ?? b.odds ?? b.totalOdds ?? '0';
  return typeof odds === 'string' ? parseInt(odds.replace('+', ''), 10) || 0 : Number(odds);
};

// =============================================================================
// Bet Stats Calculation
// =============================================================================

export function calculateBetStats(bets: Bet[] | null | undefined): BetStats {
  if (!bets || bets.length === 0) {
    return {
      totalWagered: 0,
      totalReturned: 0,
      netPL: 0,
      wins: 0,
      losses: 0,
      winRate: '0',
      roi: '0',
      biggestWin: null,
      biggestLoss: null,
      dateRange: null,
    };
  }

  // Calculate date range from bet dates (check all possible date field names)
  const validDates = bets
    .map(getBetDate)
    .filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()));
  const dateRange: DateRange | null =
    validDates.length > 0
      ? {
          from: new Date(Math.min(...validDates.map((d) => d.getTime()))),
          to: new Date(Math.max(...validDates.map((d) => d.getTime()))),
        }
      : null;

  const totalWagered = bets.reduce((sum, b) => sum + getStake(b), 0);
  const totalReturned = bets.reduce((sum, b) => sum + getReturned(b), 0);
  const netPL = totalReturned - totalWagered;
  const wins = bets.filter(isWin).length;
  const losses = bets.filter(
    (b) =>
      b.result === 'lost' ||
      (!isWin(b) && b.result !== 'pending' && b.result !== 'push' && getReturned(b) === 0),
  ).length;
  const totalBets = wins + losses;
  const winRate =
    totalBets > 0 ? formatPercent((wins / totalBets) * 100, { alreadyDecimal: true }) : '0';
  const roi =
    totalWagered > 0 ? formatPercent((netPL / totalWagered) * 100, { alreadyDecimal: true }) : '0';

  // Find biggest win (highest profit on a won bet)
  const wonBets = bets.filter(isWin);
  const biggestWin =
    wonBets.length > 0
      ? wonBets.reduce((best, b) => (getProfit(b) > getProfit(best) ? b : best))
      : null;

  // Find biggest loss (highest stake on a lost bet)
  const lostBets = bets.filter(
    (b) =>
      b.result === 'lost' ||
      (!isWin(b) && b.result !== 'pending' && b.result !== 'push' && getReturned(b) === 0),
  );
  const biggestLoss =
    lostBets.length > 0
      ? lostBets.reduce((worst, b) => (getStake(b) > getStake(worst) ? b : worst))
      : null;

  return {
    totalWagered,
    totalReturned,
    netPL,
    wins,
    losses,
    winRate,
    roi,
    biggestWin: biggestWin ? { amount: getProfit(biggestWin), name: biggestWin.name } : null,
    biggestLoss: biggestLoss ? { amount: getStake(biggestLoss), name: biggestLoss.name } : null,
    dateRange,
  };
}

// =============================================================================
// Grouping Functions
// =============================================================================

export function groupByLeague(bets: Bet[] | null | undefined): Record<string, GroupStats> {
  if (!bets || bets.length === 0) return {};

  return bets.reduce<Record<string, GroupStats>>((acc, b) => {
    const league = b.league || 'Unknown';
    if (!acc[league]) acc[league] = { wagered: 0, returned: 0, wins: 0, total: 0 };
    acc[league].wagered += getStake(b);
    acc[league].returned += getReturned(b);
    acc[league].wins += isWin(b) ? 1 : 0;
    acc[league].total += 1;
    return acc;
  }, {});
}

export function groupByLegs(bets: Bet[] | null | undefined): Record<string, GroupStats> {
  if (!bets || bets.length === 0) return {};

  return bets.reduce<Record<string, GroupStats>>((acc, b) => {
    const legCount = getLegCount(b);
    const key = legCount >= 5 ? '5+' : (legCount || 0).toString();
    if (!acc[key]) acc[key] = { wagered: 0, returned: 0, wins: 0, total: 0 };
    acc[key].wagered += getStake(b);
    acc[key].returned += getReturned(b);
    acc[key].wins += isWin(b) ? 1 : 0;
    acc[key].total += 1;
    return acc;
  }, {});
}

export function groupByOdds(bets: Bet[] | null | undefined): Record<string, GroupStats> {
  if (!bets || bets.length === 0) return {};

  return bets.reduce<Record<string, GroupStats>>((acc, b) => {
    const odds = getOdds(b);
    let key: string;
    if (odds <= 250) key = '+100 to +250';
    else if (odds <= 500) key = '+251 to +500';
    else if (odds <= 1000) key = '+501 to +1000';
    else key = '+1000+';
    if (!acc[key]) acc[key] = { wagered: 0, returned: 0, wins: 0, total: 0 };
    acc[key].wagered += getStake(b);
    acc[key].returned += getReturned(b);
    acc[key].wins += isWin(b) ? 1 : 0;
    acc[key].total += 1;
    return acc;
  }, {});
}

// =============================================================================
// Leg Stats Calculation
// =============================================================================

export function calculateLegStats(legs: Leg[]): LegStats {
  const totalLegs = legs.length;
  // Only count settled legs (hit is true or false, not null)
  const settledLegs = legs.filter((l) => l.hit !== null && l.hit !== undefined);
  const pendingLegs = legs.filter((l) => l.hit === null || l.hit === undefined);
  const totalHits = settledLegs.filter((l) => l.hit === true).length;
  const totalMisses = settledLegs.filter((l) => l.hit === false).length;
  const hitRate = settledLegs.length > 0 ? ((totalHits / settledLegs.length) * 100).toFixed(1) : 0;

  const overLegs = legs.filter((l) => l.over === true);
  const underLegs = legs.filter((l) => l.over === false);
  const settledOverLegs = overLegs.filter((l) => l.hit !== null && l.hit !== undefined);
  const settledUnderLegs = underLegs.filter((l) => l.hit !== null && l.hit !== undefined);
  const overHitRate =
    settledOverLegs.length > 0
      ? ((settledOverLegs.filter((l) => l.hit).length / settledOverLegs.length) * 100).toFixed(0)
      : 0;
  const underHitRate =
    settledUnderLegs.length > 0
      ? ((settledUnderLegs.filter((l) => l.hit).length / settledUnderLegs.length) * 100).toFixed(0)
      : 0;

  // Only include settled legs in player/prop stats
  const byPlayer = settledLegs.reduce<Record<string, PlayerStats>>((acc, l) => {
    if (!acc[l.player]) acc[l.player] = { hits: 0, total: 0 };
    acc[l.player].total++;
    if (l.hit) acc[l.player].hits++;
    return acc;
  }, {});

  const byPropType = settledLegs.reduce<Record<string, PlayerStats>>((acc, l) => {
    const key = l.over !== undefined ? `${l.prop} (${l.over ? 'Over' : 'Under'})` : l.prop;
    if (!acc[key]) acc[key] = { hits: 0, total: 0 };
    acc[key].total++;
    if (l.hit) acc[key].hits++;
    return acc;
  }, {});

  return {
    totalLegs,
    settledLegs: settledLegs.length,
    pendingLegs: pendingLegs.length,
    totalHits,
    totalMisses,
    hitRate,
    overHitRate,
    underHitRate,
    overLegs,
    underLegs,
    byPlayer,
    byPropType,
  };
}
